import { Box, Text, useToast, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import {
  parse,
  ValleyBankLoanAccountWithCollateralAndStats,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankLoanAccountQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  Alert,
  ProgressAlert,
} from 'packages/common-frontend/components/Alert';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { CollateralTable } from 'valleyBank/domains/Collateral/ui/HoldingsTable';
import { useLoanAccountUrlParam } from 'valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoanAccountNotFoundAlert } from 'valleyBank/domains/LoanAccounts/ui/Details/LoanAccountNotFoundAlert';
import { LoanAccountsDetailsTemplate } from 'valleyBank/domains/LoanAccounts/ui/Details/LoanAccountsDetailsTemplate';
import { useDataMetadataContext } from 'valleyBank/domains/page/hooks/DataMetadataContext';

import 'source/borrower/borrower.scss';

export const LoanAccountsDetailHoldingsRoute = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const { loanAccountId } = useLoanAccountUrlParam();

  const [
    loanAccount,
    setLoanAccount,
  ] = useState<ValleyBankLoanAccountWithCollateralAndStats | null>(null);

  const {
    loading: isLoading,
    error: isError,
  } = useGetValleyBankLoanAccountQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      reportingDate,
      isPreview,
      loanAccountNumber: loanAccountId || '',
    },
    onCompleted: (res) => {
      try {
        const loanAccountJson = res.valleyBankLoanAccount?.loanAccount;
        const newLoanAccount = parse.parseValleyBankLoanAccountWithCollateralAndStats(
          loanAccountJson ?? '',
        );
        if (newLoanAccount) {
          setAsOfDate(newLoanAccount.asOfDate);
          setLoanAccount(newLoanAccount);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
  });

  let routeLevelError = false;
  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load settings page`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/');
          }
        }}
      >
        <LoanAccountsDetailsTemplate selectedTab="holdings">
          <VStack spacing="40px" m="40px" alignItems="flex-start">
            {isLoading && !loanAccount && (
              <ProgressAlert>Loading loan account</ProgressAlert>
            )}
            {!isLoading && !loanAccount && isError && (
              <Alert>
                <Text>Error loading loan account</Text>
              </Alert>
            )}
            {!isLoading && !loanAccount && <LoanAccountNotFoundAlert />}
            {loanAccount && (
              <CollateralTable
                collateralAccounts={loanAccount.collateralAccounts}
                isDataError={false}
                isDataLoading={false}
                showAllocation
                ignoreCustomerColumns={false}
              />
            )}
          </VStack>
        </LoanAccountsDetailsTemplate>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
